/**
 * Participant Module Upgrade - Extended Tab Components
 * 
 * New tabs for the upgraded participant module:
 * - AddressesTab: Manage multiple addresses
 * - SupportTeamTab: Manage support team assignments
 * - PreferencesRisksTab: Preferences, risk flags, and alerts
 * - NDISFundingTab: Extended NDIS and funding details
 */

import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker } from '@/components/triniti-datetime';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useAppDialog } from '@/hooks/useAppDialog';
import { 
  Plus, Pencil, Trash2, MapPin, Users, AlertTriangle, Shield, 
  Phone, Mail, Building, UserCheck, UserX, Check, X, Home,
  FileText, Calendar, Clock, DollarSign, FileWarning, Stethoscope, 
  ClipboardList, Pill, FileQuestion, MessageSquare, CalendarDays, Tag
} from 'lucide-react';
import AsyncSearchSelect from '@/components/common/AsyncSearchSelect';
import { staffLabel, labelFor, STAFF_SEARCH_HINT } from '@/components/common/pickerLabels';

// ═══════════════════════════════════════════════════════════════════════════════
// ADDRESSES TAB
// ═══════════════════════════════════════════════════════════════════════════════

const ADDRESS_TYPES = ['HOME', 'POSTAL', 'WORK', 'RESPITE', 'EMERGENCY', 'OTHER'];
const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

export function AddressesTab({ participantId }) {
  const { dangerConfirm } = useAppDialog();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    address_type: 'HOME',
    is_primary: false,
    unit_number: '',
    street_number: '',
    street_name: '',
    street_type: '',
    suburb: '',
    state: '',
    postcode: '',
    country: 'Australia',
    access_notes: '',
    // FRD Section 6.6 - Sensitive access fields
    key_safe_details: '',
    entry_instructions: '',
    hazard_notes: '',
    service_area_notes: ''
  });

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/participants/${participantId}/addresses`)
      .then(r => setAddresses(r.data.items || []))
      .catch(() => toast.error('Failed to load addresses'))
      .finally(() => setLoading(false));
  }, [participantId]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({
      address_type: 'HOME',
      is_primary: false,
      unit_number: '',
      street_number: '',
      street_name: '',
      street_type: '',
      suburb: '',
      state: '',
      postcode: '',
      country: 'Australia',
      access_notes: '',
      key_safe_details: '',
      entry_instructions: '',
      hazard_notes: '',
      service_area_notes: ''
    });
    setEditing(null);
  };

  const buildFormattedAddress = (f) => {
    const parts = [];
    if (f.unit_number) parts.push(`Unit ${f.unit_number},`);
    if (f.street_number) parts.push(f.street_number);
    if (f.street_name) parts.push(f.street_name);
    if (f.street_type) parts.push(f.street_type);
    if (parts.length > 0 && f.suburb) parts.push(',');
    if (f.suburb) parts.push(f.suburb);
    if (f.state) parts.push(f.state);
    if (f.postcode) parts.push(f.postcode);
    return parts.join(' ').replace(/\s+/g, ' ').trim();
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, participant_id: participantId };
      payload.formatted_address = buildFormattedAddress(form);
      
      if (editing) {
        await api.put(`/participants/${participantId}/addresses/${editing.id}`, payload);
        toast.success('Address updated');
      } else {
        await api.post(`/participants/${participantId}/addresses`, payload);
        toast.success('Address created');
      }
      setShowForm(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Failed to save address');
    }
  };

  const handleDelete = async (id) => {
    const ok = await dangerConfirm({
      title: 'Delete address?',
      description: 'The address will be removed from this participant\'s record.',
      confirmLabel: 'Delete address',
      testId: 'address-delete-confirm',
    });
    if (!ok) return;
    try {
      await api.delete(`/participants/${participantId}/addresses/${id}`);
      toast.success('Address deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete address');
    }
  };

  const handleEdit = (addr) => {
    setForm({
      address_type: addr.address_type || 'HOME',
      is_primary: addr.is_primary || false,
      unit_number: addr.unit_number || '',
      street_number: addr.street_number || '',
      street_name: addr.street_name || '',
      street_type: addr.street_type || '',
      suburb: addr.suburb || '',
      state: addr.state || '',
      postcode: addr.postcode || '',
      country: addr.country || 'Australia',
      access_notes: addr.access_notes || '',
      key_safe_details: addr.key_safe_details || '',
      entry_instructions: addr.entry_instructions || '',
      hazard_notes: addr.hazard_notes || '',
      service_area_notes: addr.service_area_notes || ''
    });
    setEditing(addr);
    setShowForm(true);
  };

  if (loading) return <p className="text-muted-foreground py-4">Loading addresses...</p>;

  return (
    <div className="space-y-4" data-testid="addresses-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-bold flex items-center gap-2"><MapPin className="h-4 w-4" /> Addresses</h3>
        <Button size="sm" onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-address-btn">
          <Plus className="h-3 w-3 mr-1" /> Add Address
        </Button>
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) { setShowForm(false); resetForm(); } else setShowForm(true); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Address' : 'Add Address'}</DialogTitle>
            <DialogDescription>Enter the address details below</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Address Type</Label>
                <Select value={form.address_type} onValueChange={v => setForm({...form, address_type: v})}>
                  <SelectTrigger data-testid="addr-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ADDRESS_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={form.is_primary} 
                    onChange={e => setForm({...form, is_primary: e.target.checked})}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">Primary Address</span>
                </label>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Unit #</Label>
                <Input value={form.unit_number} onChange={e => setForm({...form, unit_number: e.target.value})} placeholder="1A" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Street #</Label>
                <Input value={form.street_number} onChange={e => setForm({...form, street_number: e.target.value})} placeholder="123" />
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Street Name</Label>
                <Input value={form.street_name} onChange={e => setForm({...form, street_name: e.target.value})} placeholder="Main" required />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Type</Label>
                <Input value={form.street_type} onChange={e => setForm({...form, street_type: e.target.value})} placeholder="St, Rd" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Suburb</Label>
                <Input value={form.suburb} onChange={e => setForm({...form, suburb: e.target.value})} placeholder="Brisbane" required />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">State</Label>
                <Select value={form.state} onValueChange={v => setForm({...form, state: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {AU_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Postcode</Label>
                <Input value={form.postcode} onChange={e => setForm({...form, postcode: e.target.value})} placeholder="4000" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Access Notes</Label>
              <Textarea 
                value={form.access_notes} 
                onChange={e => setForm({...form, access_notes: e.target.value})} 
                placeholder="e.g., Use side entrance, key under mat"
                rows={2}
              />
            </div>
            
            {/* FRD Section 6.6 - Sensitive Access Fields */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Shield className="h-4 w-4 text-amber-500" />
                Sensitive Access Information
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">Key Safe Details</Label>
                  <Input 
                    value={form.key_safe_details} 
                    onChange={e => setForm({...form, key_safe_details: e.target.value})} 
                    placeholder="e.g., Key safe code: 1234"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Entry Instructions</Label>
                  <Input 
                    value={form.entry_instructions} 
                    onChange={e => setForm({...form, entry_instructions: e.target.value})} 
                    placeholder="e.g., Ring bell twice"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-2">
                  <Label className="text-xs text-amber-600">Hazard Notes</Label>
                  <Textarea 
                    value={form.hazard_notes} 
                    onChange={e => setForm({...form, hazard_notes: e.target.value})} 
                    placeholder="e.g., Aggressive dog in yard, uneven path"
                    rows={2}
                    className="border-amber-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Service Area Notes</Label>
                  <Textarea 
                    value={form.service_area_notes} 
                    onChange={e => setForm({...form, service_area_notes: e.target.value})} 
                    placeholder="e.g., Remote area, 30min travel from office"
                    rows={2}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => { setShowForm(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" data-testid="save-address-btn">{editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Address Cards */}
      {addresses.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No addresses recorded. Click "Add Address" to add one.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {addresses.map(addr => (
            <Card key={addr.id} className={addr.is_primary ? 'border-primary' : ''}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Home className="h-4 w-4 text-muted-foreground" />
                    <Badge variant={addr.is_primary ? 'default' : 'outline'}>{addr.address_type}</Badge>
                    {addr.is_primary && <Badge variant="secondary" className="text-[10px]">Primary</Badge>}
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleEdit(addr)}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => handleDelete(addr.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <p className="mt-2 text-sm font-medium">{addr.formatted_address || '-'}</p>
                {addr.access_notes && (
                  <p className="mt-1 text-xs text-muted-foreground">Note: {addr.access_notes}</p>
                )}
                {addr.hazard_notes && (
                  <p className="mt-1 text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Hazard: {addr.hazard_notes}
                  </p>
                )}
                {addr.key_safe_details && (
                  <p className="mt-1 text-xs text-muted-foreground">Key Safe: {addr.key_safe_details}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// SUPPORT TEAM TAB
// ═══════════════════════════════════════════════════════════════════════════════

const TEAM_ROLES = [
  'KEY_WORKER', 'SUPPORT_COORDINATOR', 'CASE_MANAGER', 
  'THERAPIST', 'NURSE', 'BEHAVIOR_SUPPORT', 
  'ALLIED_HEALTH', 'PRIMARY_SUPPORT_WORKER', 'OTHER'
];

export function SupportTeamTab({ participantId }) {
  const { confirm } = useAppDialog();
  const [team, setTeam] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    member_type: 'INTERNAL_STAFF',
    staff_id: '',
    provider_name: '',
    provider_organization: '',
    provider_phone: '',
    provider_email: '',
    role: 'KEY_WORKER',
    role_description: '',
    is_primary: false,
    assignment_notes: ''
  });

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/participants/${participantId}/support-team`),
      api.get('/staff?pageSize=100')
    ])
      .then(([teamRes, staffRes]) => {
        setTeam(teamRes.data.items || []);
        setStaff(staffRes.data.items || []);
      })
      .catch(() => toast.error('Failed to load support team'))
      .finally(() => setLoading(false));
  }, [participantId]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({
      member_type: 'INTERNAL_STAFF',
      staff_id: '',
      provider_name: '',
      provider_organization: '',
      provider_phone: '',
      provider_email: '',
      role: 'KEY_WORKER',
      role_description: '',
      is_primary: false,
      assignment_notes: ''
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/participants/${participantId}/support-team`, {
        ...form,
        participant_id: participantId
      });
      toast.success('Team member added');
      setShowForm(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Failed to add team member');
    }
  };

  const handleEnd = async (memberId) => {
    const ok = await confirm({
      title: 'End team assignment?',
      description: 'The team member will be marked as ended on this participant. You can re-add them later if needed.',
      confirmLabel: 'End assignment',
      variant: 'warning',
      testId: 'team-end-assignment-confirm',
    });
    if (!ok) return;
    try {
      await api.post(`/participants/${participantId}/support-team/${memberId}/end`);
      toast.success('Assignment ended');
      load();
    } catch (e) {
      toast.error('Failed to end assignment');
    }
  };

  if (loading) return <p className="text-muted-foreground py-4">Loading support team...</p>;

  return (
    <div className="space-y-4" data-testid="support-team-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-bold flex items-center gap-2"><Users className="h-4 w-4" /> Support Team</h3>
        <Button size="sm" onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-team-member-btn">
          <Plus className="h-3 w-3 mr-1" /> Add Member
        </Button>
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>Assign a staff member or external provider</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label>Member Type</Label>
              <Select value={form.member_type} onValueChange={v => setForm({...form, member_type: v, staff_id: '', provider_name: ''})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="INTERNAL_STAFF">Internal Staff</SelectItem>
                  <SelectItem value="EXTERNAL_PROVIDER">External Provider</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.member_type === 'INTERNAL_STAFF' ? (
              <div className="space-y-2">
                <Label>Select Staff</Label>
                <AsyncSearchSelect
                  endpoint="/staff"
                  value={form.staff_id}
                  onChange={v => setForm({...form, staff_id: v})}
                  getOptionLabel={(s) => `${staffLabel(s)}${s.employee_id ? ` (${s.employee_id})` : ''}`}
                  selectedLabel={labelFor(staff, form.staff_id, staffLabel)}
                  placeholder="Choose staff member"
                  searchPlaceholder={STAFF_SEARCH_HINT}
                  emptyText="No staff match that search"
                  testId="staff-select"
                />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Provider Name</Label>
                    <Input value={form.provider_name} onChange={e => setForm({...form, provider_name: e.target.value})} required />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Organization</Label>
                    <Input value={form.provider_organization} onChange={e => setForm({...form, provider_organization: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Phone</Label>
                    <Input value={form.provider_phone} onChange={e => setForm({...form, provider_phone: e.target.value})} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Email</Label>
                    <Input type="email" value={form.provider_email} onChange={e => setForm({...form, provider_email: e.target.value})} />
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TEAM_ROLES.map(r => <SelectItem key={r} value={r}>{r.replace(/_/g, ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={form.is_primary} 
                    onChange={e => setForm({...form, is_primary: e.target.checked})}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">Primary for this role</span>
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={form.assignment_notes} 
                onChange={e => setForm({...form, assignment_notes: e.target.value})} 
                placeholder="Additional notes about this assignment"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-team-member-btn">Add Member</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Team List */}
      {team.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No support team members assigned yet.
          </CardContent>
        </Card>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {team.map(member => (
              <TableRow key={member.id}>
                <TableCell className="font-medium">
                  {member.staff_name || member.provider_name || 'Unknown'}
                  {member.is_primary && <Badge className="ml-2 text-[10px]" variant="secondary">Primary</Badge>}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{member.role?.replace(/_/g, ' ')}</Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {member.member_type === 'INTERNAL_STAFF' ? 'Staff' : 'External'}
                </TableCell>
                <TableCell className="text-xs">
                  {member.provider_phone || member.contact_number || '-'}
                </TableCell>
                <TableCell>
                  <Badge variant={member.status === 'ACTIVE' ? 'default' : 'secondary'}>
                    {member.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {member.status === 'ACTIVE' && (
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleEnd(member.id)}>
                      <UserX className="h-3 w-3 mr-1" /> End
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// PREFERENCES & RISK FLAGS TAB
// ═══════════════════════════════════════════════════════════════════════════════

const RISK_FLAG_TYPES = [
  'FALL_RISK', 'ELOPEMENT_RISK', 'AGGRESSION', 'SELF_HARM', 
  'CHOKING', 'ALLERGY', 'MEDICAL_EMERGENCY', 'WANDERING', 'SEIZURE', 'OTHER'
];
const RISK_SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

export function PreferencesRisksTab({ participantId }) {
  const { confirm } = useAppDialog();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRiskForm, setShowRiskForm] = useState(false);
  const [showAlertForm, setShowAlertForm] = useState(false);
  const [riskForm, setRiskForm] = useState({
    flag_type: 'FALL_RISK',
    severity: 'MEDIUM',
    description: '',
    mitigation: '',
    triggers: '',
    notes: ''
  });
  const [alertForm, setAlertForm] = useState({
    alert_type: 'WARNING',
    message: '',
    show_on_shift: true
  });
  const [editingRiskIdx, setEditingRiskIdx] = useState(null);
  const [editingAlertIdx, setEditingAlertIdx] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/participants/${participantId}/preferences`)
      .then(r => setPrefs(r.data.data || {}))
      .catch(() => toast.error('Failed to load preferences'))
      .finally(() => setLoading(false));
  }, [participantId]);

  useEffect(() => { load(); }, [load]);

  const handleAddRisk = async (e) => {
    e.preventDefault();
    try {
      if (editingRiskIdx != null) {
        await api.put(`/participants/${participantId}/preferences/risk-flags/${editingRiskIdx}`, riskForm);
        toast.success('Risk flag updated');
      } else {
        await api.post(`/participants/${participantId}/preferences/risk-flags`, riskForm);
        toast.success('Risk flag added');
      }
      setShowRiskForm(false);
      setEditingRiskIdx(null);
      setRiskForm({ flag_type: 'FALL_RISK', severity: 'MEDIUM', description: '', mitigation: '', triggers: '', notes: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Failed to save risk flag');
    }
  };

  const openEditRisk = (risk) => {
    setEditingRiskIdx((prefs?.risk_flags || []).indexOf(risk));
    setRiskForm({
      flag_type: risk.flag_type || 'FALL_RISK', severity: risk.severity || 'MEDIUM',
      description: risk.description || '', mitigation: risk.mitigation || '',
      triggers: risk.triggers || '', notes: risk.notes || '',
    });
    setShowRiskForm(true);
  };

  const handleAddAlert = async (e) => {
    e.preventDefault();
    try {
      if (editingAlertIdx != null) {
        await api.put(`/participants/${participantId}/preferences/alerts/${editingAlertIdx}`, alertForm);
        toast.success('Alert updated');
      } else {
        await api.post(`/participants/${participantId}/preferences/alerts`, alertForm);
        toast.success('Alert added');
      }
      setShowAlertForm(false);
      setEditingAlertIdx(null);
      setAlertForm({ alert_type: 'WARNING', message: '', show_on_shift: true });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Failed to save alert');
    }
  };

  const openEditAlert = (alert) => {
    setEditingAlertIdx((prefs?.alerts || []).indexOf(alert));
    setAlertForm({
      alert_type: alert.alert_type || 'WARNING', message: alert.message || '',
      show_on_shift: alert.show_on_shift !== false,
    });
    setShowAlertForm(true);
  };

  const handleDeleteAlert = async (alert) => {
    const idx = (prefs?.alerts || []).indexOf(alert);
    const ok = await confirm({
      title: 'Delete alert?',
      description: 'The alert will be hidden but kept in history for audit.',
      confirmLabel: 'Delete',
      variant: 'warning',
      testId: 'alert-delete-confirm',
    });
    if (!ok) return;
    try {
      await api.delete(`/participants/${participantId}/preferences/alerts/${idx}`);
      toast.success('Alert deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete alert');
    }
  };

  const handleDeactivateRisk = async (risk) => {
    // BS3-027: the list shows active flags only — send the flag's position in the FULL list (as Edit and alerts do),
    // otherwise a removed flag earlier in the list is "deactivated" again and the visible one stays.
    const index = (prefs?.risk_flags || []).indexOf(risk);
    const ok = await confirm({
      title: 'Deactivate risk flag?',
      description: 'The flag will be hidden from active risks but kept in history for audit.',
      confirmLabel: 'Deactivate',
      variant: 'warning',
      testId: 'risk-deactivate-confirm',
    });
    if (!ok) return;
    try {
      await api.delete(`/participants/${participantId}/preferences/risk-flags/${index}`);
      toast.success('Risk flag deactivated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Failed to deactivate risk flag');
    }
  };

  const severityColor = (sev) => {
    switch (sev) {
      case 'CRITICAL': return 'bg-red-600 text-white';
      case 'HIGH': return 'bg-orange-500 text-white';
      case 'MEDIUM': return 'bg-amber-500 text-white';
      default: return 'bg-green-500 text-white';
    }
  };

  if (loading) return <p className="text-muted-foreground py-4">Loading preferences...</p>;

  const activeRisks = (prefs?.risk_flags || []).filter(r => r.is_active !== false);
  const activeAlerts = (prefs?.alerts || []).filter(a => a.is_active !== false);

  return (
    <div className="space-y-6" data-testid="preferences-risks-tab">
      {/* RISK FLAGS SECTION */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> Risk Flags
          </h3>
          <Button size="sm" onClick={() => { setEditingRiskIdx(null); setRiskForm({ flag_type: 'FALL_RISK', severity: 'MEDIUM', description: '', mitigation: '', triggers: '', notes: '' }); setShowRiskForm(true); }} data-testid="add-risk-flag-btn">
            <Plus className="h-3 w-3 mr-1" /> Add Risk
          </Button>
        </div>

        {/* Risk Form Dialog */}
        <Dialog open={showRiskForm} onOpenChange={setShowRiskForm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingRiskIdx != null ? 'Edit Risk Flag' : 'Add Risk Flag'}</DialogTitle>
              <DialogDescription>Document a risk or hazard for this participant</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddRisk} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Risk Type</Label>
                  <Select value={riskForm.flag_type} onValueChange={v => setRiskForm({...riskForm, flag_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RISK_FLAG_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Severity</Label>
                  <Select value={riskForm.severity} onValueChange={v => setRiskForm({...riskForm, severity: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RISK_SEVERITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={riskForm.description} onChange={e => setRiskForm({...riskForm, description: e.target.value})} required rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Mitigation / Controls</Label>
                <Textarea value={riskForm.mitigation} onChange={e => setRiskForm({...riskForm, mitigation: e.target.value})} placeholder="How to mitigate this risk" rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Triggers</Label>
                <Input value={riskForm.triggers} onChange={e => setRiskForm({...riskForm, triggers: e.target.value})} placeholder="What triggers this risk" />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setShowRiskForm(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-risk-flag-btn">Add Risk Flag</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {activeRisks.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-center text-muted-foreground">
              No active risk flags
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {activeRisks.map((risk, idx) => (
              <Card key={idx} className={risk.severity === 'CRITICAL' ? 'border-red-300 bg-red-50/50' : risk.severity === 'HIGH' ? 'border-orange-300 bg-orange-50/50' : ''}>
                <CardContent className="py-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Badge className={severityColor(risk.severity)}>{risk.severity}</Badge>
                      <span className="font-medium">{risk.flag_type?.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditRisk(risk)} data-testid="edit-risk-flag-btn">
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => handleDeactivateRisk(risk)} data-testid="deactivate-risk-flag-btn">
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  {risk.description && <p className="mt-2 text-sm">{risk.description}</p>}
                  {risk.mitigation && <p className="mt-1 text-xs text-muted-foreground"><strong>Mitigation:</strong> {risk.mitigation}</p>}
                  {risk.triggers && <p className="text-xs text-muted-foreground"><strong>Triggers:</strong> {risk.triggers}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* ALERTS SECTION */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold flex items-center gap-2">
            <Shield className="h-4 w-4 text-blue-500" /> Alerts & Notes
          </h3>
          <Button size="sm" variant="outline" onClick={() => { setEditingAlertIdx(null); setAlertForm({ alert_type: 'WARNING', message: '', show_on_shift: true }); setShowAlertForm(true); }} data-testid="add-alert-btn">
            <Plus className="h-3 w-3 mr-1" /> Add Alert
          </Button>
        </div>

        {/* Alert Form Dialog */}
        <Dialog open={showAlertForm} onOpenChange={setShowAlertForm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingAlertIdx != null ? 'Edit Alert' : 'Add Alert'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddAlert} className="space-y-4">
              <div className="space-y-2">
                <Label>Alert Type</Label>
                <Select value={alertForm.alert_type} onValueChange={v => setAlertForm({...alertForm, alert_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="WARNING">Warning</SelectItem>
                    <SelectItem value="INFO">Info</SelectItem>
                    <SelectItem value="MEDICAL">Medical</SelectItem>
                    <SelectItem value="BEHAVIORAL">Behavioral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Message</Label>
                <Textarea value={alertForm.message} onChange={e => setAlertForm({...alertForm, message: e.target.value})} required rows={3} />
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={alertForm.show_on_shift} onChange={e => setAlertForm({...alertForm, show_on_shift: e.target.checked})} className="h-4 w-4" />
                <span className="text-sm">Show to support workers on shift</span>
              </label>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setShowAlertForm(false)}>Cancel</Button>
                <Button type="submit">Add Alert</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {activeAlerts.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-center text-muted-foreground">
              No active alerts
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {activeAlerts.map((alert, idx) => (
              <Card key={idx}>
                <CardContent className="py-3 flex items-start gap-3">
                  <Badge variant={alert.alert_type === 'WARNING' ? 'destructive' : 'secondary'}>{alert.alert_type}</Badge>
                  <div className="flex-1">
                    <p className="text-sm">{alert.message}</p>
                    {alert.show_on_shift && <span className="text-[10px] text-muted-foreground">Visible on shift</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditAlert(alert)} data-testid="edit-alert-btn">
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => handleDeleteAlert(alert)} data-testid="delete-alert-btn">
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* SUPPORT REQUIREMENTS SUMMARY */}
      {prefs?.support && Object.values(prefs.support).some(v => v) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Support Requirements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {prefs.support.mobility_support && <Badge variant="outline">Mobility Support</Badge>}
              {prefs.support.transport_support && <Badge variant="outline">Transport Support</Badge>}
              {prefs.support.personal_care_support && <Badge variant="outline">Personal Care</Badge>}
              {prefs.support.meal_support && <Badge variant="outline">Meal Support</Badge>}
              {prefs.support.medication_support && <Badge variant="outline">Medication Support</Badge>}
              {prefs.support.behavioral_support && <Badge variant="outline">Behavioral Support</Badge>}
              {prefs.support.supervision_level && <Badge>{prefs.support.supervision_level} Supervision</Badge>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* COMMUNICATION PREFERENCES */}
      {prefs?.communication && Object.values(prefs.communication).some(v => v) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Communication Preferences</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            {prefs.communication.preferred_method && <p><span className="text-muted-foreground">Preferred:</span> {prefs.communication.preferred_method}</p>}
            {prefs.communication.requires_interpreter && <Badge variant="destructive">Interpreter Required ({prefs.communication.interpreter_language || 'TBD'})</Badge>}
            {prefs.communication.communication_notes && <p className="text-xs text-muted-foreground">{prefs.communication.communication_notes}</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// NDIS FUNDING DETAILS TAB (Extended)
// ═══════════════════════════════════════════════════════════════════════════════

export function NDISFundingDetailsTab({ participantId, onEditParticipant, refreshToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    plan_number: '',
    plan_start_date: '',
    plan_end_date: '',
    plan_review_date: '',
    plan_manager: { name: '', organization: '', phone: '', email: '', abn: '' },
    support_coordinator: { name: '', organization: '', phone: '', email: '' },
    lac: { name: '', organization: '', phone: '', email: '' }
  });

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/participants/${participantId}/ndis`)
      .then(r => {
        const d = r.data.data || {};
        setData(d);
        setForm({
          plan_number: d.plan_number || '',
          plan_start_date: d.plan_start_date || '',
          plan_end_date: d.plan_end_date || '',
          plan_review_date: d.plan_review_date || '',
          plan_manager: d.plan_manager || { name: '', organization: '', phone: '', email: '', abn: '' },
          support_coordinator: d.support_coordinator || { name: '', organization: '', phone: '', email: '' },
          lac: d.lac || { name: '', organization: '', phone: '', email: '' }
        });
      })
      .catch(() => toast.error('Failed to load NDIS details'))
      .finally(() => setLoading(false));
  }, [participantId]);

  useEffect(() => { load(); }, [load, refreshToken]); // refreshToken: parent refetched participant (e.g. after Edit Participant)

  const handleSave = async () => {
    try {
      await api.put(`/participants/${participantId}/ndis`, form);
      toast.success('NDIS details updated');
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Failed to update');
    }
  };

  if (loading) return <p className="text-muted-foreground py-4">Loading NDIS details...</p>;

  // #1: rendered as a plain function, not <ContactCard/> — a component declared inside this render
  // gets a new identity on every keystroke, so React remounted the inputs and dropped focus.
  const renderContactCard = ({ title, icon: Icon, contact, fieldKey }) => (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="h-4 w-4" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {editing ? (
          <div className="space-y-2">
            <Input 
              placeholder="Name" 
              value={form[fieldKey]?.name || ''} 
              onChange={e => setForm(f => ({...f, [fieldKey]: {...f[fieldKey], name: e.target.value}}))}
            />
            <Input 
              placeholder="Organization" 
              value={form[fieldKey]?.organization || ''} 
              onChange={e => setForm(f => ({...f, [fieldKey]: {...f[fieldKey], organization: e.target.value}}))}
            />
            <div className="grid grid-cols-2 gap-2">
              <Input 
                placeholder="Phone" 
                value={form[fieldKey]?.phone || ''} 
                onChange={e => setForm(f => ({...f, [fieldKey]: {...f[fieldKey], phone: e.target.value}}))}
              />
              <Input 
                placeholder="Email" 
                value={form[fieldKey]?.email || ''} 
                onChange={e => setForm(f => ({...f, [fieldKey]: {...f[fieldKey], email: e.target.value}}))}
              />
            </div>
            {fieldKey === 'plan_manager' && (
              <Input 
                placeholder="ABN" 
                value={form[fieldKey]?.abn || ''} 
                onChange={e => setForm(f => ({...f, [fieldKey]: {...f[fieldKey], abn: e.target.value}}))}
              />
            )}
          </div>
        ) : contact?.name ? (
          <div className="text-sm space-y-1">
            <p className="font-medium">{contact.name}</p>
            {contact.organization && <p className="text-muted-foreground text-xs">{contact.organization}</p>}
            <div className="flex gap-3 text-xs text-muted-foreground">
              {contact.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{contact.phone}</span>}
              {contact.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{contact.email}</span>}
            </div>
            {contact.abn && <p className="text-xs text-muted-foreground">ABN: {contact.abn}</p>}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Not assigned</p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="ndis-details-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-bold flex items-center gap-2">
          <FileText className="h-4 w-4" /> NDIS Details
        </h3>
        {editing ? (
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); load(); }}>Cancel</Button>
            <Button size="sm" onClick={handleSave} data-testid="save-ndis-btn">Save Changes</Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-ndis-btn">
            <Pencil className="h-3 w-3 mr-1" /> Edit
          </Button>
        )}
      </div>

      {/* Basic NDIS Info */}
      <Card>
        <CardContent className="py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">NDIS Number</p>
              <p className="font-mono font-medium" data-testid="ndis-details-number">{data?.ndis_number || '-'}</p>
              {onEditParticipant && (
                <button type="button" onClick={onEditParticipant} className="text-xs text-primary underline mt-1" data-testid="ndis-edit-participant-link">
                  Edit participant details
                </button>
              )}
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Funding Type</p>
              <Badge variant="outline">{data?.funding_type?.replace(/_/g, ' ') || 'Not Set'}</Badge>
            </div>
            {editing ? (
              <>
                <div>
                  <Label className="text-xs">Plan Start</Label>
                  <TrinitiDatePicker value={form.plan_start_date} onChange={v => setForm({...form, plan_start_date: v})} testId="ndis-plan-start" />
                </div>
                <div>
                  <Label className="text-xs">Plan End</Label>
                  <TrinitiDatePicker value={form.plan_end_date} onChange={v => setForm({...form, plan_end_date: v})} testId="ndis-plan-end" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <p className="text-xs text-muted-foreground">Plan Start</p>
                  <p className="font-medium">{data?.plan_start_date || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Plan End</p>
                  <p className="font-medium">{data?.plan_end_date || '-'}</p>
                </div>
              </>
            )}
          </div>
          {editing && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <Label className="text-xs">Plan Number</Label>
                <Input value={form.plan_number} onChange={e => setForm({...form, plan_number: e.target.value})} placeholder="Optional plan number" />
              </div>
              <div>
                <Label className="text-xs">Plan Review Date</Label>
                <TrinitiDatePicker value={form.plan_review_date} onChange={v => setForm({...form, plan_review_date: v})} testId="ndis-plan-review" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Key Contacts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {renderContactCard({ title: 'Plan Manager', icon: Building, contact: data?.plan_manager, fieldKey: 'plan_manager' })}
        {renderContactCard({ title: 'Support Coordinator', icon: UserCheck, contact: data?.support_coordinator, fieldKey: 'support_coordinator' })}
        {renderContactCard({ title: 'Local Area Coordinator', icon: Users, contact: data?.lac, fieldKey: 'lac' })}
      </div>
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════════════
// CONSENTS TAB (Phase 5)
// ═══════════════════════════════════════════════════════════════════════════════

const CONSENT_TYPES = [
  'PORTAL_ACCESS', 'FAMILY_ACCESS', 'DOCUMENT_ACCESS',
  'PHOTO_CONSENT', 'MEDIA_CONSENT', 'INFORMATION_SHARING',
  'SERVICE_AGREEMENT', 'MEDICATION_MANAGEMENT', 'RESTRICTIVE_PRACTICE',
  'TRANSPORT_CONSENT', 'COMMUNITY_ACCESS', 'FINANCIAL_MANAGEMENT',
  'EMERGENCY_TREATMENT', 'DIGITAL_SIGNATURE', 'GUARDIAN_AUTHORITY'
];

export function ConsentsTab({ participantId }) {
  const { dangerConfirm } = useAppDialog();
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [renewState, setRenewState] = useState({ open: false, consentId: null, expiry: '' });
  const [viewConsent, setViewConsent] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    consent_type: 'SERVICE_AGREEMENT',
    consent_details: '',
    expires_at: '',
    renewal_required: false,
    renewal_date: '',
    signed_by: '',
    signed_at: '',
    witness_name: ''
  });

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/participants/${participantId}/consents`)
      .then(r => setConsents(r.data.items || []))
      .catch(() => toast.error('Failed to load consents'))
      .finally(() => setLoading(false));
  }, [participantId]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({
      consent_type: 'SERVICE_AGREEMENT',
      consent_details: '',
      expires_at: '',
      renewal_required: false,
      renewal_date: '',
      signed_by: '',
      signed_at: '',
      witness_name: ''
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/participants/${participantId}/consents/${editingId}`, { ...form });
        toast.success('Consent updated');
      } else {
        await api.post(`/participants/${participantId}/consents`, {
          participant_id: participantId,
          ...form
        });
        toast.success('Consent created');
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || `Failed to ${editingId ? 'update' : 'create'} consent`);
    }
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      consent_type: c.consentType || 'SERVICE_AGREEMENT',
      consent_details: c.consent_details || '',
      expires_at: c.expiresAt ? c.expiresAt.split('T')[0] : '',
      renewal_required: !!c.renewal_required,
      renewal_date: c.renewal_date ? c.renewal_date.split('T')[0] : '',
      signed_by: c.signed_by || '',
      signed_at: c.signed_at ? c.signed_at.split('T')[0] : '',
      witness_name: c.witness_name || ''
    });
    setShowForm(true);
  };

  const handleRevoke = async (consentId) => {
    const ok = await dangerConfirm({
      title: 'Revoke this consent?',
      description: 'The consent will be marked as revoked. The audit trail records this change.',
      confirmLabel: 'Revoke consent',
      reasonRequired: true,
      testId: 'consent-revoke-confirm',
    });
    if (!ok) return;
    try {
      await api.post(`/participants/${participantId}/consents/${consentId}/revoke`);
      toast.success('Consent revoked');
      load();
    } catch (e) {
      toast.error('Failed to revoke consent');
    }
  };

  const openRenew = (consentId) => {
    const def = new Date();
    def.setFullYear(def.getFullYear() + 1);
    const yyyy = def.getFullYear();
    const mm = String(def.getMonth() + 1).padStart(2, '0');
    const dd = String(def.getDate()).padStart(2, '0');
    setRenewState({ open: true, consentId, expiry: `${yyyy}-${mm}-${dd}` });
  };

  const submitRenew = async () => {
    const { consentId, expiry } = renewState;
    if (!consentId || !expiry) return;
    try {
      await api.post(`/participants/${participantId}/consents/${consentId}/renew?new_expiry=${expiry}`);
      toast.success('Consent renewed');
      setRenewState({ open: false, consentId: null, expiry: '' });
      load();
    } catch (e) {
      toast.error('Failed to renew consent');
    }
  };

  const handleRenew = (consentId) => openRenew(consentId);

  const statusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-500 text-white';
      case 'REVOKED': return 'bg-red-500 text-white';
      case 'EXPIRED': return 'bg-gray-500 text-white';
      default: return 'bg-amber-500 text-white';
    }
  };

  if (loading) return <p className="text-muted-foreground py-4">Loading consents...</p>;

  return (
    <div className="space-y-4" data-testid="consents-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-bold flex items-center gap-2">
          <FileText className="h-4 w-4" /> Consents
        </h3>
        <Button size="sm" onClick={() => { setEditingId(null); resetForm(); setShowForm(true); }} data-testid="add-consent-btn">
          <Plus className="h-3 w-3 mr-1" /> Add Consent
        </Button>
      </div>

      {/* Create Form Dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) setEditingId(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Consent Record' : 'Create Consent Record'}</DialogTitle>
            <DialogDescription>Document a consent for this participant</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label>Consent Type</Label>
              <Select value={form.consent_type} onValueChange={v => setForm({...form, consent_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONSENT_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Details</Label>
              <Textarea 
                value={form.consent_details} 
                onChange={e => setForm({...form, consent_details: e.target.value})} 
                placeholder="Describe what is being consented to"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Signed By</Label>
                <Input value={form.signed_by} onChange={e => setForm({...form, signed_by: e.target.value})} placeholder="Name of person signing" />
              </div>
              <div className="space-y-2">
                <Label>Signed At</Label>
                <TrinitiDatePicker value={form.signed_at} onChange={v => setForm({...form, signed_at: v})} testId="consent-signed-at" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Expires At</Label>
                <TrinitiDatePicker value={form.expires_at} onChange={v => setForm({...form, expires_at: v})} testId="consent-expires-at" />
              </div>
              <div className="space-y-2">
                <Label>Witness Name</Label>
                <Input value={form.witness_name} onChange={e => setForm({...form, witness_name: e.target.value})} />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={form.renewal_required} 
                  onChange={e => setForm({...form, renewal_required: e.target.checked})}
                  className="h-4 w-4"
                />
                <span className="text-sm">Requires Renewal</span>
              </label>
              {form.renewal_required && (
                <div className="flex-1">
                  <TrinitiDatePicker 
                    value={form.renewal_date} 
                    onChange={v => setForm({...form, renewal_date: v})}
                    testId="consent-renewal-date"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</Button>
              <Button type="submit" data-testid="save-consent-btn">{editingId ? 'Save Changes' : 'Create Consent'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Renew Consent dialog (replaces window.prompt) */}
      <Dialog open={renewState.open} onOpenChange={(v) => { if (!v) setRenewState({ open: false, consentId: null, expiry: '' }); }}>
        <DialogContent className="max-w-sm" data-testid="consent-renew-dialog">
          <DialogHeader>
            <DialogTitle>Renew Consent</DialogTitle>
            <DialogDescription>Pick a new expiry date for this consent.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-xs">New expiry date</Label>
            <TrinitiDatePicker
              value={renewState.expiry}
              onChange={v => setRenewState(s => ({ ...s, expiry: v }))}
              testId="consent-renew-date"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewState({ open: false, consentId: null, expiry: '' })}>Cancel</Button>
            <Button onClick={submitRenew} disabled={!renewState.expiry} data-testid="consent-renew-submit">Renew</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Consents List */}
      {consents.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No consent records found
          </CardContent>
        </Card>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Granted</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Signed By</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {consents.map(c => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.consentType?.replace(/_/g, ' ')}</TableCell>
                <TableCell><Badge className={statusColor(c.status)}>{c.status}</Badge></TableCell>
                <TableCell className="text-xs">{c.grantedAt?.split('T')[0] || '-'}</TableCell>
                <TableCell className="text-xs">{c.expiresAt?.split('T')[0] || 'No Expiry'}</TableCell>
                <TableCell className="text-xs">{c.signed_by || '-'}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => setViewConsent(c)} data-testid={`consent-view-${c.id}`}>View</Button>
                  {c.status === 'ACTIVE' && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`consent-edit-${c.id}`}>Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => handleRenew(c.id)}>Renew</Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleRevoke(c.id)}>Revoke</Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!viewConsent} onOpenChange={(o) => { if (!o) setViewConsent(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="consent-view-dialog">
          <DialogHeader>
            <DialogTitle>{viewConsent?.consentType?.replace(/_/g, ' ')}</DialogTitle>
            <DialogDescription>Consent record details</DialogDescription>
          </DialogHeader>
          {viewConsent && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge className={statusColor(viewConsent.status)}>{viewConsent.status}</Badge></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Granted</span><span>{viewConsent.grantedAt?.split('T')[0] || '—'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Expires</span><span>{viewConsent.expiresAt?.split('T')[0] || 'No expiry'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Signed by</span><span>{viewConsent.signed_by || '—'}</span></div>
              {viewConsent.relationship && <div className="flex justify-between"><span className="text-muted-foreground">Relationship</span><span>{viewConsent.relationship}</span></div>}
              {viewConsent.revokedAt && <div className="flex justify-between"><span className="text-muted-foreground">Revoked</span><span>{viewConsent.revokedAt.split('T')[0]}</span></div>}
              {viewConsent.notes && <div><p className="text-muted-foreground mb-1">Notes</p><p className="whitespace-pre-wrap rounded border p-2 bg-muted/30">{viewConsent.notes}</p></div>}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setViewConsent(null)}>Close</Button>
            {viewConsent?.status === 'ACTIVE' && <Button onClick={() => { const id = viewConsent.id; setViewConsent(null); handleRenew(id); }} data-testid="consent-view-renew">Renew</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// EXPIRING ITEMS TAB (Phase 5)
// ═══════════════════════════════════════════════════════════════════════════════

export function ExpiringItemsTab({ participantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [daysAhead, setDaysAhead] = useState(30);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/participants/${participantId}/expiring-items?days_ahead=${daysAhead}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load expiring items'))
      .finally(() => setLoading(false));
  }, [participantId, daysAhead]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-muted-foreground py-4">Loading expiring items...</p>;

  const items = data?.data || {};
  const summary = data?.summary || {};
  const hasExpiring = summary.expiring_consents > 0 || summary.expiring_documents > 0 || summary.ndis_plan_expiring;

  return (
    <div className="space-y-4" data-testid="expiring-items-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-bold flex items-center gap-2">
          <Calendar className="h-4 w-4 text-amber-500" /> Expiring Items
        </h3>
        <Select value={String(daysAhead)} onValueChange={v => setDaysAhead(Number(v))}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Next 7 days</SelectItem>
            <SelectItem value="14">Next 14 days</SelectItem>
            <SelectItem value="30">Next 30 days</SelectItem>
            <SelectItem value="60">Next 60 days</SelectItem>
            <SelectItem value="90">Next 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!hasExpiring ? (
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="py-8 text-center">
            <Check className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <p className="text-green-700 font-medium">No items expiring in the next {daysAhead} days</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4">
            <Card className={summary.ndis_plan_expiring ? 'border-red-300 bg-red-50/50' : ''}>
              <CardContent className="py-4 text-center">
                <p className="text-2xl font-bold">{items.ndis_plan ? items.ndis_plan.days_remaining : '-'}</p>
                <p className="text-xs text-muted-foreground">NDIS Plan Days Left</p>
              </CardContent>
            </Card>
            <Card className={summary.expiring_consents > 0 ? 'border-amber-300 bg-amber-50/50' : ''}>
              <CardContent className="py-4 text-center">
                <p className="text-2xl font-bold">{summary.expiring_consents}</p>
                <p className="text-xs text-muted-foreground">Consents Expiring</p>
              </CardContent>
            </Card>
            <Card className={summary.expiring_documents > 0 ? 'border-amber-300 bg-amber-50/50' : ''}>
              <CardContent className="py-4 text-center">
                <p className="text-2xl font-bold">{summary.expiring_documents}</p>
                <p className="text-xs text-muted-foreground">Documents Expiring</p>
              </CardContent>
            </Card>
          </div>

          {/* NDIS Plan Alert */}
          {items.ndis_plan && (
            <Card className="border-red-300">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-red-600 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" /> NDIS Plan Expiring
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">Plan ends on <strong>{items.ndis_plan.plan_end_date}</strong></p>
                <p className="text-xs text-muted-foreground mt-1">Contact support coordinator to arrange plan review</p>
              </CardContent>
            </Card>
          )}

          {/* Expiring Consents */}
          {items.consents?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Expiring Consents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {items.consents.map(c => (
                    <div key={c.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                      <div>
                        <p className="text-sm font-medium">{c.consentType?.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-muted-foreground">Expires: {c.expiresAt?.split('T')[0]}</p>
                      </div>
                      <Badge variant="outline" className="text-amber-600">Expiring</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Expiring Documents */}
          {items.documents?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Expiring Documents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {items.documents.map(d => (
                    <div key={d.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                      <div>
                        <p className="text-sm font-medium">{d.document_category?.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-muted-foreground">Expires: {d.expiry_date}</p>
                      </div>
                      <Badge variant="outline" className="text-amber-600">Expiring</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// DUPLICATE DETECTION TAB (Phase 6)
// ═══════════════════════════════════════════════════════════════════════════════

export function DuplicateDetectionTab({ participantId, onMergeComplete }) {
  const { dangerConfirm } = useAppDialog();
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDupe, setSelectedDupe] = useState(null);
  const [mergePreview, setMergePreview] = useState(null);
  const [merging, setMerging] = useState(false);
  const [mergeReason, setMergeReason] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get(`/participants/${participantId}/potential-duplicates`)
      .then(r => setDuplicates(r.data.data || []))
      .catch(() => toast.error('Failed to check for duplicates'))
      .finally(() => setLoading(false));
  }, [participantId]);

  const handlePreviewMerge = async (targetId) => {
    try {
      const r = await api.post(`/participants/${participantId}/merge-preview?target_participant_id=${targetId}`);
      setMergePreview(r.data.data);
      setSelectedDupe(targetId);
      setMergeReason('');
    } catch (e) {
      toast.error('Failed to generate merge preview');
    }
  };

  const handleExecuteMerge = async () => {
    const ok = await dangerConfirm({
      title: 'Merge these participants?',
      description: 'The source participant will be archived and all selected data merged into the target. This cannot be undone.',
      confirmLabel: 'Merge participants',
      requireTypeToConfirm: 'MERGE',
      reasonRequired: true,
      testId: 'participant-merge-confirm',
    });
    if (!ok) {
      return;
    }
    
    setMerging(true);
    try {
      const params = new URLSearchParams({
        target_participant_id: selectedDupe,
        merge_contacts: 'true',
        merge_addresses: 'true',
        merge_documents: 'true',
        merge_goals: 'true',
        merge_notes: 'true'
      });
      if (mergeReason) {
        params.append('merge_reason', mergeReason);
      }
      
      await api.post(`/participants/${participantId}/merge?${params.toString()}`);
      toast.success('Participants merged successfully. Source participant has been archived.');
      setMergePreview(null);
      setSelectedDupe(null);
      // Callback to parent to navigate or refresh
      if (onMergeComplete) {
        onMergeComplete(selectedDupe);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Failed to merge participants');
    } finally {
      setMerging(false);
    }
  };

  if (loading) return <p className="text-muted-foreground py-4">Checking for potential duplicates...</p>;

  return (
    <div className="space-y-4" data-testid="duplicates-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-bold flex items-center gap-2">
          <Users className="h-4 w-4" /> Potential Duplicates
        </h3>
      </div>

      {duplicates.length === 0 ? (
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="py-8 text-center">
            <Check className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <p className="text-green-700 font-medium">No potential duplicates found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{duplicates.length} potential duplicate(s) found based on name, DOB, email, or phone</p>
          
          {duplicates.map(dupe => (
            <Card key={dupe.id} className={selectedDupe === dupe.id ? 'border-primary' : ''}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{dupe.first_name} {dupe.last_name}</p>
                      <Badge variant="outline" className="text-amber-600">{dupe.match_score}% Match</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      NDIS: {dupe.ndis_number || '-'} | DOB: {dupe.date_of_birth || '-'}
                    </p>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {dupe.match_reasons?.map((r, i) => (
                        <Badge key={i} variant="secondary" className="text-[10px]">{r}</Badge>
                      ))}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handlePreviewMerge(dupe.id)}>
                    Preview Merge
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Merge Preview Dialog */}
      <Dialog open={!!mergePreview} onOpenChange={() => setMergePreview(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Merge Preview</DialogTitle>
            <DialogDescription>Review what will happen if these participants are merged</DialogDescription>
          </DialogHeader>
          {mergePreview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Source (will be merged FROM)</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p className="font-medium">{mergePreview.source?.first_name} {mergePreview.source?.last_name}</p>
                    <p className="text-xs text-muted-foreground">NDIS: {mergePreview.source?.ndis_number}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Target (will be kept)</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p className="font-medium">{mergePreview.target?.first_name} {mergePreview.target?.last_name}</p>
                    <p className="text-xs text-muted-foreground">NDIS: {mergePreview.target?.ndis_number}</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Records to Move</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg font-bold text-center">{mergePreview.total_records_to_move} records</p>
                  <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                    {Object.entries(mergePreview.source_record_counts || {}).map(([key, val]) => (
                      val > 0 && <div key={key} className="text-center p-2 bg-muted/30 rounded">
                        <p className="font-medium">{val}</p>
                        <p className="text-muted-foreground">{key.replace(/_/g, ' ')}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {mergePreview.conflicts?.length > 0 && (
                <Card className="border-amber-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-amber-600">Field Conflicts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Field</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>Target</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {mergePreview.conflicts.map((c, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium">{c.field}</TableCell>
                            <TableCell className="text-xs">{c.source_value}</TableCell>
                            <TableCell className="text-xs">{c.target_value}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              <div className="space-y-2">
                <Label>Merge Reason (Optional)</Label>
                <Textarea 
                  value={mergeReason} 
                  onChange={(e) => setMergeReason(e.target.value)}
                  placeholder="Why are you merging these participants?"
                  rows={2}
                />
              </div>

              <DialogFooter className="flex gap-2">
                <Button 
                  variant="ghost" 
                  onClick={() => setMergePreview(null)}
                  disabled={merging}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleExecuteMerge}
                  disabled={merging}
                  data-testid="execute-merge-btn"
                >
                  {merging ? 'Merging...' : 'Confirm Merge'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════════════
// LINKED MODULES TAB (FRD Section 6.12)
// ═══════════════════════════════════════════════════════════════════════════════

export function LinkedModulesTab({ participantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/participants/${participantId}/linked-modules`)
      .then(r => setData(r.data.data))
      .catch(() => toast.error('Failed to load linked modules'))
      .finally(() => setLoading(false));
  }, [participantId]);

  if (loading) return <p className="text-muted-foreground py-4">Loading linked modules...</p>;
  if (!data) return <p className="text-muted-foreground py-4">No data available</p>;

  const ModuleCard = ({ title, icon: Icon, count, label, color, items, link }) => (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => link && window.open(link, '_self')}>
      <CardContent className="py-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${color}`}>
              <Icon className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-sm">{title}</p>
              <p className="text-2xl font-bold">{count}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
          </div>
        </div>
        {items && items.length > 0 && (
          <div className="mt-3 pt-3 border-t space-y-1">
            {items.slice(0, 2).map((item, i) => (
              <p key={i} className="text-xs text-muted-foreground truncate">
                {item.date || item.status || item.type || '-'}
              </p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="linked-modules-tab">
      <h3 className="font-bold">Linked Modules</h3>
      
      {/* Primary Modules */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <ModuleCard 
          title="Roster" 
          icon={CalendarDays}
          count={data.roster?.upcoming_shifts || 0}
          label="Upcoming Shifts"
          color="bg-blue-500"
          items={data.roster?.recent_shifts}
        />
        <ModuleCard 
          title="Timesheets" 
          icon={Clock}
          count={data.timesheets?.total_hours_month || 0}
          label="Hours This Month"
          color="bg-green-500"
        />
        <ModuleCard 
          title="Invoices" 
          icon={DollarSign}
          count={data.invoices?.unpaid_count || 0}
          label={`$${(data.invoices?.total_outstanding || 0).toLocaleString()} Outstanding`}
          color="bg-amber-500"
          items={data.invoices?.recent}
        />
        <ModuleCard 
          title="Claims" 
          icon={FileText}
          count={data.claims?.pending_count || 0}
          label="Pending Claims"
          color="bg-purple-500"
          items={data.claims?.recent}
        />
      </div>

      {/* Secondary Modules */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <ModuleCard 
          title="Incidents" 
          icon={FileWarning}
          count={data.incidents?.open_count || 0}
          label="Open Incidents"
          color={data.incidents?.open_count > 0 ? "bg-red-500" : "bg-gray-400"}
          items={data.incidents?.recent}
        />
        <ModuleCard 
          title="Care Plans" 
          icon={ClipboardList}
          count={data.care_plans?.active_count || 0}
          label="Active Plans"
          color="bg-teal-500"
        />
        <ModuleCard 
          title="Progress Notes" 
          icon={FileText}
          count={data.progress_notes?.this_week || 0}
          label="Notes This Week"
          color="bg-indigo-500"
        />
        <ModuleCard 
          title="Accommodation" 
          icon={Home}
          count={data.accommodation?.current ? 1 : 0}
          label={data.accommodation?.current?.house_name || 'Not Assigned'}
          color="bg-orange-500"
        />
      </div>

      {/* Tertiary Modules */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <ModuleCard 
          title="Medications" 
          icon={Pill}
          count={data.medications?.active_count || 0}
          label="Active Medications"
          color="bg-pink-500"
        />
        <ModuleCard 
          title="Forms" 
          icon={FileQuestion}
          count={data.forms?.pending_count || 0}
          label="Pending Forms"
          color={data.forms?.pending_count > 0 ? "bg-amber-500" : "bg-gray-400"}
        />
        <ModuleCard 
          title="Messages" 
          icon={MessageSquare}
          count={data.messages?.unread_count || 0}
          label="Unread Messages"
          color={data.messages?.unread_count > 0 ? "bg-blue-600" : "bg-gray-400"}
        />
        <Card className="opacity-50">
          <CardContent className="py-4 text-center">
            <p className="text-xs text-muted-foreground">More modules coming soon</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Activity Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            <div>
              <p className="text-xl font-bold">{data.roster?.upcoming_shifts || 0}</p>
              <p className="text-xs text-muted-foreground">Upcoming Shifts</p>
            </div>
            <div>
              <p className="text-xl font-bold">{data.timesheets?.pending_count || 0}</p>
              <p className="text-xs text-muted-foreground">Pending Timesheets</p>
            </div>
            <div>
              <p className="text-xl font-bold text-amber-600">${(data.invoices?.total_outstanding || 0).toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Outstanding</p>
            </div>
            <div>
              <p className="text-xl font-bold text-red-600">{data.incidents?.open_count || 0}</p>
              <p className="text-xs text-muted-foreground">Open Incidents</p>
            </div>
            <div>
              <p className="text-xl font-bold">{data.progress_notes?.this_week || 0}</p>
              <p className="text-xs text-muted-foreground">Notes This Week</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// OVERVIEW SUMMARY COMPONENT (FRD Section 5)
// ═══════════════════════════════════════════════════════════════════════════════

export function OverviewSummaryCards({ participantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/participants/${participantId}/overview-summary`)
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [participantId]);

  if (loading || !data) return null;

  const fundingHealthColor = (status) => {
    switch (status) {
      case 'HEALTHY': return 'bg-green-100 text-green-800 border-green-200';
      case 'WARNING': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'CRITICAL': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6" data-testid="overview-summary-cards">
      {/* Status */}
      <Card>
        <CardContent className="py-3 text-center">
          <p className="text-xs text-muted-foreground">Status</p>
          <Badge className="mt-1">{data.status}</Badge>
        </CardContent>
      </Card>

      {/* Onboarding */}
      <Card>
        <CardContent className="py-3 text-center">
          <p className="text-xs text-muted-foreground">Onboarding</p>
          <Badge variant="outline" className="mt-1">{data.onboarding_status?.replace(/_/g, ' ')}</Badge>
        </CardContent>
      </Card>

      {/* NDIS End Date */}
      <Card className={data.ndis_end_date && new Date(data.ndis_end_date) < new Date(Date.now() + 30*24*60*60*1000) ? 'border-amber-300' : ''}>
        <CardContent className="py-3 text-center">
          <p className="text-xs text-muted-foreground">NDIS Plan Ends</p>
          <p className="font-semibold text-sm mt-1">{data.ndis_end_date || 'Not Set'}</p>
        </CardContent>
      </Card>

      {/* Funding Health */}
      <Card className={fundingHealthColor(data.funding_health_status)}>
        <CardContent className="py-3 text-center">
          <p className="text-xs">Funding Health</p>
          <p className="font-bold text-sm mt-1">{data.funding_health_status}</p>
          {data.remaining_budget && <p className="text-xs">${data.remaining_budget.toLocaleString()} left</p>}
        </CardContent>
      </Card>

      {/* Service Agreement */}
      <Card className={data.service_agreement_status === 'EXPIRED' ? 'border-red-300' : ''}>
        <CardContent className="py-3 text-center">
          <p className="text-xs text-muted-foreground">Service Agreement</p>
          <Badge variant={data.service_agreement_status === 'SIGNED' ? 'default' : 'outline'} className="mt-1">
            {data.service_agreement_status?.replace(/_/g, ' ')}
          </Badge>
        </CardContent>
      </Card>

      {/* Alerts */}
      <Card className={data.alerts_count > 0 ? 'border-red-300 bg-red-50/50' : ''}>
        <CardContent className="py-3 text-center">
          <p className="text-xs text-muted-foreground">Active Alerts</p>
          <p className={`font-bold text-xl ${data.alerts_count > 0 ? 'text-red-600' : ''}`}>{data.alerts_count}</p>
        </CardContent>
      </Card>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// ENHANCED GOALS TAB (FRD Section 6.8)
// ═══════════════════════════════════════════════════════════════════════════════

const GOAL_CATEGORIES = [
  'DAILY_LIVING', 'COMMUNITY_PARTICIPATION', 'INDEPENDENCE', 
  'EMPLOYMENT', 'HEALTH_WELLBEING', 'BEHAVIOUR_SUPPORT',
  'COMMUNICATION', 'ACCOMMODATION', 'TRANSPORT', 
  'SOCIAL_CONNECTION', 'OTHER'
];

const GOAL_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

const GOAL_STATUSES = [
  'NOT_STARTED', 'IN_PROGRESS', 'ON_TRACK', 
  'AT_RISK', 'ACHIEVED', 'PAUSED', 'CANCELLED'
];

const getStatusColor = (status) => {
  const colors = {
    'NOT_STARTED': 'bg-gray-100 text-gray-700',
    'IN_PROGRESS': 'bg-blue-100 text-blue-700',
    'ON_TRACK': 'bg-green-100 text-green-700',
    'AT_RISK': 'bg-amber-100 text-amber-700',
    'ACHIEVED': 'bg-emerald-100 text-emerald-700',
    'PAUSED': 'bg-gray-100 text-gray-500',
    'CANCELLED': 'bg-red-100 text-red-700'
  };
  return colors[status] || 'bg-gray-100 text-gray-700';
};

const getPriorityColor = (priority) => {
  const colors = {
    'LOW': 'bg-slate-100 text-slate-600',
    'MEDIUM': 'bg-blue-100 text-blue-700',
    'HIGH': 'bg-orange-100 text-orange-700',
    'CRITICAL': 'bg-red-100 text-red-700'
  };
  return colors[priority] || 'bg-gray-100';
};

export function GoalsExtendedTab({ participantId }) {
  const { dangerConfirm } = useAppDialog();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [form, setForm] = useState({
    goal_title: '',
    goal_category: 'INDEPENDENCE',
    goal_description: '',
    target_date: '',
    review_date: '',
    priority: 'MEDIUM',
    progress_status: 'NOT_STARTED',
    progress_percent: 0,
    progress_notes: '',
    outcome_measure: '',
    responsible_staff_id: ''
  });

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/participants/${participantId}/goals`)
      .then(r => setGoals(r.data.data || []))
      .catch(() => toast.error('Failed to load goals'))
      .finally(() => setLoading(false));
  }, [participantId]);

  useEffect(() => {
    load();
    // Load staff list for responsible_staff_id dropdown
    api.get('/staff?pageSize=100&status=ACTIVE')
      .then(r => setStaffList(r.data.items || []))
      .catch(() => {});
  }, [load]);

  const resetForm = () => {
    setForm({
      goal_title: '', goal_category: 'INDEPENDENCE', goal_description: '',
      target_date: '', review_date: '', priority: 'MEDIUM',
      progress_status: 'NOT_STARTED', progress_percent: 0,
      progress_notes: '', outcome_measure: '', responsible_staff_id: ''
    });
    setEditing(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/participants/${participantId}/goals/${editing}`, form);
        toast.success('Goal updated');
      } else {
        await api.post(`/participants/${participantId}/goals`, { participant_id: participantId, ...form });
        toast.success('Goal created');
      }
      load();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Failed to save goal');
    }
  };

  const handleEdit = (goal) => {
    setForm({
      goal_title: goal.goal_title || '',
      goal_category: goal.goal_category || 'INDEPENDENCE',
      goal_description: goal.goal_description || '',
      target_date: goal.target_date || '',
      review_date: goal.review_date || '',
      priority: goal.priority || 'MEDIUM',
      progress_status: goal.progress_status || 'NOT_STARTED',
      progress_percent: goal.progress_percent || 0,
      progress_notes: goal.progress_notes || '',
      outcome_measure: goal.outcome_measure || '',
      responsible_staff_id: goal.responsible_staff_id || ''
    });
    setEditing(goal.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const ok = await dangerConfirm({
      title: 'Delete this goal?',
      description: 'The goal and its progress history will be permanently removed.',
      confirmLabel: 'Delete goal',
      testId: 'goal-delete-confirm',
    });
    if (!ok) return;
    try {
      await api.delete(`/participants/${participantId}/goals/${id}`);
      toast.success('Goal deleted');
      load();
    } catch (err) {
      toast.error('Failed to delete goal');
    }
  };

  if (loading) return <div className="py-8 text-center text-muted-foreground">Loading goals...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Goals & Outcomes</h3>
        <Button size="sm" onClick={() => setShowForm(true)} data-testid="add-goal-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Goal
        </Button>
      </div>

      {/* Goal Form */}
      {showForm && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">{editing ? 'Edit Goal' : 'New Goal'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Goal Title *</Label>
                  <Input
                    value={form.goal_title}
                    onChange={(e) => setForm({ ...form, goal_title: e.target.value })}
                    required
                    placeholder="Enter goal title"
                    data-testid="goal-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={form.goal_category} onValueChange={(v) => setForm({ ...form, goal_category: v })}>
                    <SelectTrigger data-testid="goal-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GOAL_CATEGORIES.map(c => (
                        <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={form.goal_description}
                  onChange={(e) => setForm({ ...form, goal_description: e.target.value })}
                  placeholder="Describe the goal..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GOAL_PRIORITIES.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={form.progress_status} onValueChange={(v) => setForm({ ...form, progress_status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GOAL_STATUSES.map(s => (
                        <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Target Date</Label>
                  <TrinitiDatePicker value={form.target_date} onChange={(v) => setForm({ ...form, target_date: v })} testId="goal-target-date" />
                </div>
                <div className="space-y-2">
                  <Label>Review Date</Label>
                  <TrinitiDatePicker value={form.review_date} onChange={(v) => setForm({ ...form, review_date: v })} testId="goal-review-date" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Progress ({form.progress_percent}%)</Label>
                  <Input
                    type="range"
                    min="0"
                    max="100"
                    value={form.progress_percent}
                    onChange={(e) => setForm({ ...form, progress_percent: parseInt(e.target.value) })}
                    className="h-8"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Outcome Measure</Label>
                  <Input
                    value={form.outcome_measure}
                    onChange={(e) => setForm({ ...form, outcome_measure: e.target.value })}
                    placeholder="How will success be measured?"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Responsible Staff</Label>
                  <AsyncSearchSelect
                    endpoint="/staff"
                    params={{ status: 'ACTIVE' }}
                    value={form.responsible_staff_id}
                    onChange={(v) => setForm({ ...form, responsible_staff_id: v || '' })}
                    getOptionLabel={staffLabel}
                    selectedLabel={labelFor(staffList, form.responsible_staff_id, staffLabel)}
                    placeholder="Select staff..."
                    searchPlaceholder={STAFF_SEARCH_HINT}
                    emptyText="No staff match that search"
                    clearLabel="None"
                    testId="responsible-staff-select"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Progress Notes</Label>
                <Textarea
                  value={form.progress_notes}
                  onChange={(e) => setForm({ ...form, progress_notes: e.target.value })}
                  placeholder="Notes on progress..."
                  rows={2}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" size="sm" data-testid="save-goal-btn">
                  {editing ? 'Update Goal' : 'Create Goal'}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Goals List */}
      {goals.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No goals defined yet. Add goals to track outcomes.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {goals.map(goal => (
            <Card key={goal.id} className="hover:border-primary/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-medium">{goal.goal_title}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {goal.goal_category?.replace(/_/g, ' ')}
                      </Badge>
                      <Badge className={`text-xs ${getPriorityColor(goal.priority)}`}>
                        {goal.priority}
                      </Badge>
                      <Badge className={`text-xs ${getStatusColor(goal.progress_status)}`}>
                        {goal.progress_status?.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(goal)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(goal.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                {goal.goal_description && (
                  <p className="text-sm text-muted-foreground mb-3">{goal.goal_description}</p>
                )}

                {/* Progress Bar */}
                <div className="mb-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span>Progress</span>
                    <span>{goal.progress_percent || 0}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${goal.progress_percent || 0}%` }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 text-xs text-muted-foreground">
                  <div>
                    <span className="block font-medium text-foreground">Target</span>
                    {goal.target_date || '-'}
                  </div>
                  <div>
                    <span className="block font-medium text-foreground">Review</span>
                    {goal.review_date || '-'}
                  </div>
                  <div>
                    <span className="block font-medium text-foreground">Outcome</span>
                    {goal.outcome_measure || '-'}
                  </div>
                  <div>
                    <span className="block font-medium text-foreground">Responsible</span>
                    {goal.responsible_staff_name || '-'}
                  </div>
                </div>

                {goal.progress_notes && (
                  <div className="mt-3 p-2 bg-muted/50 rounded text-xs">
                    <span className="font-medium">Notes:</span> {goal.progress_notes}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAGS TAB (FRD Section 10.11)
// ═══════════════════════════════════════════════════════════════════════════════

export function TagsTab({ participantId }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTag, setNewTag] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/participants/${participantId}/tags`)
      .then(r => setTags(r.data.data || []))
      .catch(() => toast.error('Failed to load tags'))
      .finally(() => setLoading(false));
  }, [participantId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newTag.trim()) return;
    
    setAdding(true);
    try {
      await api.post(`/participants/${participantId}/tags`, { tag: newTag.trim() });
      toast.success('Tag added');
      setNewTag('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Failed to add tag');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (tagId) => {
    try {
      await api.delete(`/participants/${participantId}/tags/${tagId}`);
      toast.success('Tag removed');
      load();
    } catch (err) {
      toast.error('Failed to remove tag');
    }
  };

  if (loading) return <div className="py-8 text-center text-muted-foreground">Loading tags...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Participant Tags</h3>
      </div>

      {/* Add Tag Form */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleAdd} className="flex gap-2">
            <Input
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              placeholder="Enter tag name..."
              className="flex-1"
              data-testid="new-tag-input"
            />
            <Button type="submit" size="sm" disabled={adding || !newTag.trim()} data-testid="add-tag-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Tag
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Tags Display */}
      <Card>
        <CardContent className="p-4">
          {tags.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              No tags assigned. Add tags to categorize and filter participants.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {tags.map(tag => (
                <Badge
                  key={tag.id}
                  variant="secondary"
                  className="px-3 py-1 text-sm flex items-center gap-2"
                >
                  {tag.tag}
                  <button
                    onClick={() => handleRemove(tag.id)}
                    className="hover:text-red-500 transition-colors"
                    data-testid={`remove-tag-${tag.tag}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tag Usage Info */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground">
            <strong>Tip:</strong> Tags help you organize participants into groups for filtering and reporting.
            Common tags include: High Support, SIL, Community Access, Behaviour Support, etc.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════════════
// FLAGS TAB - FRD Section 10.5
// ═══════════════════════════════════════════════════════════════════════════════

const FLAG_TYPES = [
  'ALLERGY',
  'MEDICATION_ALERT',
  'MANUAL_HANDLING_ALERT',
  'BEHAVIOUR_SUPPORT_ALERT',
  'RESTRICTIVE_PRACTICE_ALERT',
  'MOBILITY_ALERT',
  'INFECTION_ALERT',
  'COMMUNICATION_ALERT',
  'MISSING_DOCUMENTS',
  'FUNDING_EXPIRING',
  'SERVICE_AGREEMENT_EXPIRING',
  'INCIDENT_WATCH',
  'NO_EMERGENCY_CONTACT',
  'CONSENT_EXPIRED'
];

const FLAG_SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

const FLAG_SEVERITY_COLORS = {
  LOW: 'bg-blue-100 text-blue-700 border-blue-200',
  MEDIUM: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  HIGH: 'bg-orange-100 text-orange-700 border-orange-200',
  CRITICAL: 'bg-red-100 text-red-700 border-red-200'
};

export function FlagsTab({ participantId }) {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    flag_type: 'ALLERGY',
    severity: 'MEDIUM',
    message: '',
    details: '',
    expires_at: ''
  });

  const load = useCallback(() => {
    setLoading(true);
    // Get all flags, not just active ones
    api.get(`/participants/${participantId}/flags?active_only=false`)
      .then(r => setFlags(r.data.data || []))
      .catch(() => toast.error('Failed to load flags'))
      .finally(() => setLoading(false));
  }, [participantId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams({
        flag_type: form.flag_type,
        severity: form.severity,
        notes: form.message || ''
      });
      if (editingId) {
        await api.patch(`/participants/${participantId}/flags/${editingId}?${params.toString()}`);
        toast.success('Flag updated');
      } else {
        await api.post(`/participants/${participantId}/flags?${params.toString()}`);
        toast.success('Flag created');
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ flag_type: 'ALLERGY', severity: 'MEDIUM', message: '', details: '', expires_at: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || `Failed to ${editingId ? 'update' : 'create'} flag`);
    }
  };

  const openEdit = (flag) => {
    setEditingId(flag.id);
    setForm({
      flag_type: flag.flag_type || 'ALLERGY',
      severity: flag.severity || 'MEDIUM',
      message: flag.notes || '',
      details: '',
      expires_at: ''
    });
    setShowForm(true);
  };

  const handleResolve = async (flagId) => {
    try {
      await api.patch(`/participants/${participantId}/flags/${flagId}?is_active=false`);
      toast.success('Flag resolved');
      load();
    } catch (err) {
      toast.error('Failed to resolve flag');
    }
  };

  if (loading) return <div className="py-8 text-center text-muted-foreground">Loading flags...</div>;

  const activeFlags = flags.filter(f => f.is_active !== false);
  const resolvedFlags = flags.filter(f => f.is_active === false);

  return (
    <div className="space-y-4" data-testid="flags-tab">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-semibold">Participant Flags & Alerts</h3>
          <p className="text-sm text-muted-foreground">System-generated and manual flags for attention</p>
        </div>
        <Button size="sm" onClick={() => { setEditingId(null); setForm({ flag_type: 'ALLERGY', severity: 'MEDIUM', message: '', details: '', expires_at: '' }); setShowForm(true); }} data-testid="add-flag-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Flag
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-3">
        {FLAG_SEVERITIES.map(sev => {
          const count = activeFlags.filter(f => f.severity === sev).length;
          return (
            <Card key={sev} className={count > 0 ? FLAG_SEVERITY_COLORS[sev] : ''}>
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-xs">{sev}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Active Flags */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-500" />
            Active Flags ({activeFlags.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeFlags.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">No active flags. Great!</p>
          ) : (
            <div className="space-y-2">
              {activeFlags.map(flag => (
                <div 
                  key={flag.id} 
                  className={`p-3 rounded-lg border ${FLAG_SEVERITY_COLORS[flag.severity] || ''}`}
                  data-testid={`flag-${flag.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{flag.flag_type?.replace(/_/g, ' ')}</Badge>
                        <Badge className={FLAG_SEVERITY_COLORS[flag.severity]}>{flag.severity}</Badge>
                      </div>
                      <p className="font-medium mt-1">{flag.notes || flag.flag_type?.replace(/_/g, ' ')}</p>
                      {flag.source_module && <p className="text-sm text-muted-foreground">Source: {flag.source_module}</p>}
                      <p className="text-xs text-muted-foreground mt-1">
                        Created: {flag.created_at?.slice(0, 10)}
                        {flag.end_date && ` · Ends: ${flag.end_date.slice(0, 10)}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => openEdit(flag)}
                        data-testid={`edit-flag-${flag.id}`}
                      >
                        <Pencil className="h-3 w-3 mr-1" /> Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleResolve(flag.id)}
                        data-testid={`resolve-flag-${flag.id}`}
                      >
                        <Check className="h-3 w-3 mr-1" /> Resolve
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resolved Flags */}
      {resolvedFlags.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-muted-foreground">
              <Check className="h-4 w-4" />
              Resolved Flags ({resolvedFlags.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {resolvedFlags.slice(0, 5).map(flag => (
                <div key={flag.id} className="p-2 rounded border bg-muted/30 opacity-60">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{flag.flag_type?.replace(/_/g, ' ')}</Badge>
                    <span className="text-sm">{flag.notes || flag.flag_type?.replace(/_/g, ' ')}</span>
                    <span className="text-xs text-muted-foreground ml-auto">
                      Resolved: {flag.end_date?.slice(0, 10) || 'N/A'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add / Edit Flag Dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) setEditingId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Flag' : 'Add Flag'}</DialogTitle>
            <DialogDescription>{editingId ? 'Update this flag for the participant' : 'Create a manual flag for this participant'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Flag Type</Label>
                <Select value={form.flag_type} onValueChange={v => setForm({...form, flag_type: v})}>
                  <SelectTrigger data-testid="flag-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FLAG_TYPES.map(t => (
                      <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Severity</Label>
                <Select value={form.severity} onValueChange={v => setForm({...form, severity: v})}>
                  <SelectTrigger data-testid="flag-severity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FLAG_SEVERITIES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes *</Label>
              <Textarea 
                value={form.message} 
                onChange={e => setForm({...form, message: e.target.value})}
                placeholder="Description of the flag and any relevant notes"
                required
                rows={3}
                data-testid="flag-message-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</Button>
              <Button type="submit" disabled={!form.message} data-testid="save-flag-btn">{editingId ? 'Save Changes' : 'Create Flag'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// TIMELINE EVENTS TAB - FRD Section 10.10
// ═══════════════════════════════════════════════════════════════════════════════

const EVENT_TYPE_ICONS = {
  PARTICIPANT_CREATED: Users,
  STATUS_CHANGE: Shield,
  DOCUMENT_UPLOADED: FileText,
  CONSENT_GRANTED: Check,
  CONSENT_REVOKED: X,
  GOAL_CREATED: ClipboardList,
  GOAL_UPDATED: ClipboardList,
  RISK_FLAG_ADDED: AlertTriangle,
  SHIFT_COMPLETED: Calendar,
  NOTE_ADDED: MessageSquare,
  APPOINTMENT_SCHEDULED: CalendarDays,
  MEDICATION_STARTED: Pill,
  FUNDING_UPDATED: DollarSign,
  MERGE_EVENT: Users,
  MANUAL: Clock
};

const EVENT_TYPE_COLORS = {
  PARTICIPANT_CREATED: 'bg-green-100 text-green-700',
  STATUS_CHANGE: 'bg-purple-100 text-purple-700',
  DOCUMENT_UPLOADED: 'bg-blue-100 text-blue-700',
  CONSENT_GRANTED: 'bg-green-100 text-green-700',
  CONSENT_REVOKED: 'bg-red-100 text-red-700',
  GOAL_CREATED: 'bg-indigo-100 text-indigo-700',
  GOAL_UPDATED: 'bg-indigo-100 text-indigo-700',
  RISK_FLAG_ADDED: 'bg-orange-100 text-orange-700',
  SHIFT_COMPLETED: 'bg-cyan-100 text-cyan-700',
  NOTE_ADDED: 'bg-slate-100 text-slate-700',
  APPOINTMENT_SCHEDULED: 'bg-pink-100 text-pink-700',
  MEDICATION_STARTED: 'bg-amber-100 text-amber-700',
  FUNDING_UPDATED: 'bg-emerald-100 text-emerald-700',
  MERGE_EVENT: 'bg-purple-100 text-purple-700',
  MANUAL: 'bg-gray-100 text-gray-700'
};

export function TimelineEventsTab({ participantId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  const load = useCallback(() => {
    setLoading(true);
    let url = `/participants/${participantId}/timeline-events?limit=100`;
    if (filter && filter !== 'ALL') url += `&event_type=${filter}`;
    if (dateRange.from) url += `&from_date=${dateRange.from}`;
    if (dateRange.to) url += `&to_date=${dateRange.to}`;
    
    api.get(url)
      .then(r => setEvents(r.data.data || []))
      .catch(() => toast.error('Failed to load timeline'))
      .finally(() => setLoading(false));
  }, [participantId, filter, dateRange]);

  useEffect(() => { load(); }, [load]);

  // Group events by date
  const groupedEvents = events.reduce((acc, event) => {
    const date = event.event_time?.slice(0, 10) || 'Unknown';
    if (!acc[date]) acc[date] = [];
    acc[date].push(event);
    return acc;
  }, {});

  const eventTypes = ['ALL', ...Object.keys(EVENT_TYPE_ICONS)];

  if (loading) return <div className="py-8 text-center text-muted-foreground">Loading timeline...</div>;

  return (
    <div className="space-y-4" data-testid="timeline-events-tab">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-semibold">Activity Timeline</h3>
          <p className="text-sm text-muted-foreground">Audit trail of all participant events</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3">
          <div className="flex gap-3 items-end flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Event Type</Label>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger data-testid="timeline-filter">
                  <SelectValue placeholder="All Events" />
                </SelectTrigger>
                <SelectContent>
                  {eventTypes.map(t => (
                    <SelectItem key={t} value={t}>{t === 'ALL' ? 'All Events' : t.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From Date</Label>
              <TrinitiDatePicker 
                value={dateRange.from} 
                onChange={v => setDateRange({...dateRange, from: v})}
                className="w-40"
              />
            </div>
            <div>
              <Label className="text-xs">To Date</Label>
              <TrinitiDatePicker 
                value={dateRange.to} 
                onChange={v => setDateRange({...dateRange, to: v})}
                className="w-40"
              />
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => { setFilter('ALL'); setDateRange({ from: '', to: '' }); }}
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      {events.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <Clock className="h-10 w-10 mx-auto mb-2 opacity-40" />
            <p>No events found for the selected filters</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedEvents).sort(([a], [b]) => b.localeCompare(a)).map(([date, dateEvents]) => (
            <div key={date}>
              <div className="sticky top-0 bg-background py-2 z-10">
                <Badge variant="outline" className="font-mono">
                  {new Date(date).toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </Badge>
              </div>
              <div className="space-y-2 ml-4 border-l-2 border-muted pl-4">
                {dateEvents.map((event, idx) => {
                  const Icon = EVENT_TYPE_ICONS[event.event_type] || Clock;
                  const colorClass = EVENT_TYPE_COLORS[event.event_type] || 'bg-gray-100 text-gray-700';
                  
                  return (
                    <div key={event.id || idx} className="relative" data-testid={`timeline-event-${event.id}`}>
                      <div className="absolute -left-[1.35rem] top-2 w-3 h-3 rounded-full bg-muted border-2 border-background" />
                      <Card className="hover:shadow-sm transition-shadow">
                        <CardContent className="p-3">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${colorClass}`}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className="text-xs">
                                  {event.event_type?.replace(/_/g, ' ')}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {event.event_time?.slice(11, 16)}
                                </span>
                              </div>
                              <p className="font-medium mt-1">{event.title || event.description}</p>
                              {event.description && event.title && (
                                <p className="text-sm text-muted-foreground">{event.description}</p>
                              )}
                              {event.metadata && Object.keys(event.metadata).length > 0 && (
                                <div className="mt-2 text-xs text-muted-foreground bg-muted/30 rounded p-2">
                                  {Object.entries(event.metadata).map(([k, v]) => (
                                    <span key={k} className="mr-3">{k}: <strong>{String(v)}</strong></span>
                                  ))}
                                </div>
                              )}
                              {event.created_by_name && (
                                <p className="text-xs text-muted-foreground mt-1">By: {event.created_by_name}</p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      <Card className="bg-muted/30">
        <CardContent className="p-3">
          <p className="text-xs text-muted-foreground">
            Showing <strong>{events.length}</strong> events
            {filter !== 'ALL' && <span> (filtered by {filter.replace(/_/g, ' ')})</span>}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
